import React, { useEffect, useState } from 'react';
import { 
  getAllUsers, UserData, saveSystemConfig, getSystemConfig, 
  getUserTransactions, adminAdjustCredits, adminUpdatePlan 
} from '../services/firebase';
import { Transaction, UserTier } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useSystem } from '../contexts/SystemContext';
import { 
  Users, DollarSign, Activity, Search, ShieldAlert, Lock, Crown, 
  Settings, Save, Loader2, ExternalLink, HelpCircle, Eye, 
  MoreHorizontal, X, Plus, Minus, CreditCard, History, Calendar, BarChart3
} from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const { role } = useAuth();
  const { refreshConfig } = useSystem();
  const [activeTab, setActiveTab] = useState<'users' | 'settings'>('users');
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Settings State
  const [paypalMode, setPaypalMode] = useState<'sandbox' | 'production'>('sandbox');
  const [sandboxId, setSandboxId] = useState('');
  const [prodId, setProdId] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  // User Management State
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [userTransactions, setUserTransactions] = useState<Transaction[]>([]);
  const [loadingTrans, setLoadingTrans] = useState(false);
  
  // Sub-modals state
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [creditAmount, setCreditAmount] = useState<number>(0);
  const [creditReason, setCreditReason] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    if (role === 'admin') {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [role]);

  const fetchDashboardData = async () => {
    setLoading(true);
    const data = await getAllUsers();
    setUsers(data);
    
    const config = await getSystemConfig();
    if (config) {
      setPaypalMode(config.paypalMode);
      setSandboxId(config.paypalSandboxClientId || '');
      setProdId(config.paypalProductionClientId || '');
    }
    setLoading(false);
  };

  const handleOpenUser = async (user: UserData) => {
    setSelectedUser(user);
    setLoadingTrans(true);
    const trans = await getUserTransactions(user.uid);
    setUserTransactions(trans);
    setLoadingTrans(false);
  };

  const handleCloseUser = () => {
    setSelectedUser(null);
    setUserTransactions([]);
    setShowCreditModal(false);
    setShowPlanModal(false);
  };

  // --- ACTIONS ---

  const handleAdjustCredits = async (isAddition: boolean) => {
    if (!selectedUser) return;
    if (creditAmount <= 0) {
        alert("Amount must be greater than 0");
        return;
    }
    const finalAmount = isAddition ? creditAmount : -creditAmount;
    
    setProcessingAction(true);
    try {
        await adminAdjustCredits(selectedUser.uid, finalAmount, creditReason || (isAddition ? 'Admin Bonus' : 'Admin Penalty'));
        // Refresh local data
        const updatedTrans = await getUserTransactions(selectedUser.uid);
        setUserTransactions(updatedTrans);
        
        // Update user in list locally
        setUsers(prev => prev.map(u => u.uid === selectedUser.uid ? { ...u, credits: u.credits + finalAmount } : u));
        setSelectedUser(prev => prev ? { ...prev, credits: prev.credits + finalAmount } : null);
        
        setShowCreditModal(false);
        setCreditAmount(0);
        setCreditReason('');
    } catch (e) {
        alert("Failed to adjust credits");
    } finally {
        setProcessingAction(false);
    }
  };

  const handleChangePlan = async (newTier: UserTier) => {
      if (!selectedUser) return;
      if (!window.confirm(`Are you sure you want to change this user's plan to ${newTier}?`)) return;

      setProcessingAction(true);
      try {
          await adminUpdatePlan(selectedUser.uid, newTier);
          
           // Refresh local data
          const updatedTrans = await getUserTransactions(selectedUser.uid);
          setUserTransactions(updatedTrans);

          // Update user in list locally
          setUsers(prev => prev.map(u => u.uid === selectedUser.uid ? { ...u, tier: newTier } : u));
          setSelectedUser(prev => prev ? { ...prev, tier: newTier } : null);
          
          setShowPlanModal(false);
      } catch (e) {
          alert("Failed to change plan");
      } finally {
          setProcessingAction(false);
      }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await saveSystemConfig({
        paypalMode,
        paypalSandboxClientId: sandboxId,
        paypalProductionClientId: prodId
      });
      await refreshConfig(); 
      alert("Settings saved!");
    } catch (e) {
      alert("Error saving settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  if (role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center h-screen w-full bg-gray-950 p-8 text-center">
        <div className="bg-red-500/20 p-6 rounded-full mb-6">
          <Lock className="w-16 h-16 text-red-500" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-2">Access Denied</h2>
        <p className="text-gray-400 max-w-md text-lg">You do not have permission.</p>
      </div>
    );
  }

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalCredits = users.reduce((acc, curr) => acc + (curr.credits || 0), 0);
  const totalApiCost = users.reduce((acc, curr) => acc + (curr.stats?.estimatedCostUSD || 0), 0);

  // --- RENDER HELPERS ---
  const renderTierBadge = (tier: string = 'free') => {
      const colors: any = {
          free: 'bg-gray-700 text-gray-400 border-gray-600',
          starter: 'bg-green-900/40 text-green-300 border-green-800',
          creator: 'bg-blue-900/40 text-blue-300 border-blue-800',
          pro: 'bg-yellow-900/40 text-yellow-300 border-yellow-800',
      };
      return (
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${colors[tier] || colors.free}`}>
              {tier}
          </span>
      );
  };

  return (
    <div className="min-h-screen bg-gray-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        
        {/* HEADER */}
        <header className="flex flex-col md:flex-row justify-between items-center mb-8 pb-8 border-b border-gray-800 gap-4">
          <div className="flex items-center gap-4">
             <div className="bg-red-900/30 p-3 rounded-2xl border border-red-500/20">
               <ShieldAlert className="w-8 h-8 text-red-500" />
             </div>
             <div>
               <h2 className="text-3xl font-bold text-white">Admin Portal</h2>
               <p className="text-gray-400 text-sm">System Overview & User Management</p>
             </div>
          </div>
          <div className="flex gap-2 bg-gray-900/50 p-1 rounded-xl border border-white/10">
            <button 
              onClick={() => setActiveTab('users')}
              className={`px-6 py-2 rounded-lg font-bold transition-all text-sm ${activeTab === 'users' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
            >
              Users
            </button>
            <button 
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-2 rounded-lg font-bold transition-all text-sm flex items-center gap-2 ${activeTab === 'settings' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
            >
              <Settings className="w-4 h-4" /> Settings
            </button>
          </div>
        </header>

        {/* CONTENT TABS */}
        {activeTab === 'users' && (
          <>
            {/* STATS CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-900/50 backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-xl flex items-center justify-between group hover:border-blue-500/30 transition-all">
                  <div>
                    <p className="text-gray-400 text-sm font-medium uppercase tracking-wider">Total Users</p>
                    <h3 className="text-4xl font-bold text-white mt-1 group-hover:text-blue-400 transition-colors">{users.length}</h3>
                  </div>
                  <div className="p-4 bg-blue-500/10 rounded-2xl">
                    <Users className="w-8 h-8 text-blue-400" />
                  </div>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-xl flex items-center justify-between group hover:border-green-500/30 transition-all">
                  <div>
                    <p className="text-gray-400 text-sm font-medium uppercase tracking-wider">Global Credits</p>
                    <h3 className="text-4xl font-bold text-white mt-1 group-hover:text-green-400 transition-colors">{totalCredits}</h3>
                  </div>
                  <div className="p-4 bg-green-500/10 rounded-2xl">
                    <Activity className="w-8 h-8 text-green-400" />
                  </div>
              </div>

               <div className="bg-gray-900/50 backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-xl flex items-center justify-between group hover:border-red-500/30 transition-all">
                  <div>
                    <p className="text-gray-400 text-sm font-medium uppercase tracking-wider">Est. API Cost</p>
                    <h3 className="text-4xl font-bold text-white mt-1 group-hover:text-red-400 transition-colors">${totalApiCost.toFixed(2)}</h3>
                  </div>
                  <div className="p-4 bg-red-500/10 rounded-2xl">
                    <DollarSign className="w-8 h-8 text-red-400" />
                  </div>
              </div>
            </div>

            {/* USERS TABLE */}
            <div className="bg-gray-900/50 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
              <div className="p-6 border-b border-white/5 flex flex-col md:flex-row justify-between gap-4 items-center">
                <h3 className="text-xl font-bold text-white">Registered Users</h3>
                <div className="relative w-full md:w-auto">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input 
                    type="text" 
                    placeholder="Search users..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-gray-800 border border-gray-700 text-sm rounded-lg pl-10 pr-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none w-full md:w-64"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-gray-800/50 text-gray-400 text-xs uppercase tracking-wider">
                      <th className="px-6 py-4 font-medium">User</th>
                      <th className="px-6 py-4 font-medium">Status</th>
                      <th className="px-6 py-4 font-medium">Credits</th>
                      <th className="px-6 py-4 font-medium">API Usage</th>
                      <th className="px-6 py-4 font-medium">Joined</th>
                      <th className="px-6 py-4 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredUsers.map((user) => (
                      <tr key={user.uid} className="hover:bg-white/5 transition-colors group">
                        <td className="px-6 py-4 flex items-center gap-3">
                          {user.photoURL ? (
                            <img src={user.photoURL} alt="" className="w-9 h-9 rounded-full bg-gray-700 object-cover" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-300">{user.displayName ? user.displayName[0] : 'U'}</div>
                          )}
                          <div className="flex flex-col">
                              <span className="font-medium text-gray-200">{user.displayName || 'Anonymous'}</span>
                              <span className="text-xs text-gray-500">{user.email}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                           {renderTierBadge(user.tier)}
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-mono text-gray-300 bg-gray-800 px-2 py-1 rounded border border-gray-700">{user.credits}</span>
                        </td>
                        <td className="px-6 py-4">
                           <div className="flex flex-col gap-0.5">
                               <span className="text-xs text-red-300 font-mono">${user.stats?.estimatedCostUSD?.toFixed(3) || '0.000'}</span>
                               <span className="text-[10px] text-gray-500">{(user.stats?.totalRequests || 0)} requests</span>
                           </div>
                        </td>
                        <td className="px-6 py-4 text-gray-500 text-xs">
                          {user.createdAt?.seconds ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-right">
                           <button 
                             onClick={() => handleOpenUser(user)}
                             className="p-2 bg-gray-800 hover:bg-blue-600 text-gray-400 hover:text-white rounded-lg transition-all"
                             title="Manage User"
                           >
                             <MoreHorizontal className="w-4 h-4" />
                           </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ... Settings Tab ... */}
        {activeTab === 'settings' && (
          <div className="bg-gray-900/50 backdrop-blur-xl border border-white/10 p-8 rounded-2xl shadow-xl max-w-2xl mx-auto">
             <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-blue-500/10 rounded-xl">
                  <DollarSign className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                   <h3 className="text-2xl font-bold text-white">Payment Gateway</h3>
                   <p className="text-gray-400 text-sm">Configure PayPal for handling payments.</p>
                </div>
             </div>
             
             <div className="space-y-6">
               <div className="p-4 bg-blue-900/20 rounded-xl border border-blue-500/20 flex gap-3 items-start">
                  <HelpCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-200">
                    <p>Go to <a href="https://developer.paypal.com/dashboard/applications/sandbox" target="_blank" rel="noreferrer" className="underline hover:text-white inline-flex items-center gap-1">PayPal Developer <ExternalLink className="w-3 h-3" /></a></p>
                    <p className="mt-1 opacity-80">Create an App, then copy the <strong>Client ID</strong> (not Secret) from "Apps & Credentials".</p>
                  </div>
               </div>

               <div>
                 <label className="block text-sm font-medium text-gray-400 mb-2">Environment Mode</label>
                 <div className="flex gap-4">
                   <button 
                     onClick={() => setPaypalMode('sandbox')}
                     className={`flex-1 py-3 px-4 rounded-xl border transition-all ${paypalMode === 'sandbox' ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300' : 'bg-gray-800 border-gray-700 text-gray-400'}`}
                   >
                     Sandbox (Test)
                   </button>
                   <button 
                     onClick={() => setPaypalMode('production')}
                     className={`flex-1 py-3 px-4 rounded-xl border transition-all ${paypalMode === 'production' ? 'bg-green-500/20 border-green-500 text-green-300' : 'bg-gray-800 border-gray-700 text-gray-400'}`}
                   >
                     Production (Live)
                   </button>
                 </div>
               </div>

               <div className="grid gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Sandbox Client ID</label>
                    <input 
                        type="text" 
                        value={sandboxId}
                        onChange={(e) => setSandboxId(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none font-mono text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Production Client ID</label>
                    <input 
                        type="text" 
                        value={prodId}
                        onChange={(e) => setProdId(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none font-mono text-xs"
                    />
                  </div>
               </div>

               <button 
                 onClick={handleSaveSettings}
                 disabled={savingSettings}
                 className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
               >
                 {savingSettings ? <Loader2 className="animate-spin w-5 h-5" /> : <Save className="w-5 h-5" />}
                 Save Configuration
               </button>
             </div>
          </div>
        )}

      </div>

      {/* --- USER DETAIL MODAL --- */}
      {selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
             <div className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity" onClick={handleCloseUser}></div>
             
             <div className="relative z-10 w-full max-w-4xl bg-gray-900 border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
                {/* Modal Header */}
                <div className="p-6 border-b border-white/5 bg-gray-900 flex justify-between items-start">
                   <div className="flex items-center gap-4">
                      {selectedUser.photoURL ? (
                          <img src={selectedUser.photoURL} alt="" className="w-16 h-16 rounded-full border-2 border-gray-700" />
                      ) : (
                          <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center text-xl font-bold">{selectedUser.displayName ? selectedUser.displayName[0] : 'U'}</div>
                      )}
                      <div>
                          <h3 className="text-2xl font-bold text-white">{selectedUser.displayName}</h3>
                          <p className="text-gray-400 text-sm">{selectedUser.email}</p>
                          <div className="flex gap-2 mt-2">
                             <span className="text-xs bg-gray-800 px-2 py-0.5 rounded text-gray-400 border border-gray-700">UID: {selectedUser.uid.substring(0,8)}...</span>
                             {renderTierBadge(selectedUser.tier)}
                          </div>
                      </div>
                   </div>
                   <button onClick={handleCloseUser} className="p-2 hover:bg-white/10 rounded-full text-gray-400 hover:text-white">
                      <X className="w-6 h-6" />
                   </button>
                </div>

                {/* API Usage Stats Row */}
                <div className="grid grid-cols-3 gap-1 bg-gray-950/30 p-4 border-b border-white/5">
                    <div className="bg-gray-800/40 p-3 rounded-xl border border-white/5">
                        <p className="text-[10px] uppercase text-gray-500 font-bold mb-1">Lifetime Requests</p>
                        <p className="text-lg font-mono text-white">{selectedUser.stats?.totalRequests || 0}</p>
                    </div>
                    <div className="bg-gray-800/40 p-3 rounded-xl border border-white/5">
                         <p className="text-[10px] uppercase text-gray-500 font-bold mb-1">Total Tokens</p>
                         <p className="text-lg font-mono text-blue-300">
                           {((selectedUser.stats?.totalInputTokens || 0) + (selectedUser.stats?.totalOutputTokens || 0)).toLocaleString()}
                         </p>
                    </div>
                    <div className="bg-gray-800/40 p-3 rounded-xl border border-white/5">
                         <p className="text-[10px] uppercase text-gray-500 font-bold mb-1">Estimated Cost</p>
                         <p className="text-lg font-mono text-red-400">${selectedUser.stats?.estimatedCostUSD?.toFixed(4) || '0.0000'}</p>
                    </div>
                </div>

                {/* Modal Actions Toolbar */}
                <div className="p-4 bg-gray-800/50 border-b border-white/5 flex gap-3 overflow-x-auto">
                    <button onClick={() => setShowCreditModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600/10 text-blue-400 border border-blue-500/20 rounded-lg hover:bg-blue-600/20 transition-colors whitespace-nowrap">
                        <DollarSign className="w-4 h-4" /> Adjust Balance
                    </button>
                    <button onClick={() => setShowPlanModal(true)} className="flex items-center gap-2 px-4 py-2 bg-purple-600/10 text-purple-400 border border-purple-500/20 rounded-lg hover:bg-purple-600/20 transition-colors whitespace-nowrap">
                        <Crown className="w-4 h-4" /> Change Plan
                    </button>
                </div>

                {/* Modal Body: Transaction History */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-950/50">
                   <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <History className="w-4 h-4" /> Transaction History
                   </h4>

                   {loadingTrans ? (
                       <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-gray-500" /></div>
                   ) : userTransactions.length === 0 ? (
                       <div className="text-center p-8 text-gray-500 border-2 border-dashed border-gray-800 rounded-xl">No transactions found.</div>
                   ) : (
                       <div className="space-y-3">
                           {userTransactions.map(t => (
                               <div key={t.id} className="flex items-center justify-between p-4 bg-gray-900 border border-white/5 rounded-xl hover:border-white/10 transition-colors">
                                   <div className="flex items-center gap-4">
                                       <div className={`p-2 rounded-lg ${
                                           t.type === 'purchase' ? 'bg-green-500/20 text-green-400' :
                                           t.type === 'bonus' ? 'bg-blue-500/20 text-blue-400' :
                                           t.type === 'penalty' ? 'bg-red-500/20 text-red-400' :
                                           'bg-gray-700 text-gray-400'
                                       }`}>
                                           {t.type === 'purchase' ? <DollarSign className="w-4 h-4" /> :
                                            t.type === 'bonus' ? <Plus className="w-4 h-4" /> :
                                            t.type === 'penalty' ? <Minus className="w-4 h-4" /> :
                                            <Activity className="w-4 h-4" />}
                                       </div>
                                       <div>
                                           <p className="text-gray-200 font-medium text-sm">{t.description}</p>
                                           <p className="text-gray-500 text-xs">
                                               {t.timestamp?.seconds ? new Date(t.timestamp.seconds * 1000).toLocaleString() : 'Just now'}
                                           </p>
                                       </div>
                                   </div>
                                   <div className={`font-mono font-bold ${t.amount > 0 ? 'text-green-400' : t.amount < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                                       {t.amount > 0 ? '+' : ''}{t.amount} Cr
                                   </div>
                               </div>
                           ))}
                       </div>
                   )}
                </div>
             </div>
          </div>
      )}

      {/* --- CREDIT ADJUSTMENT MODAL --- */}
      {showCreditModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreditModal(false)}></div>
              <div className="relative z-10 w-full max-w-sm bg-gray-900 border border-white/10 rounded-2xl p-6 shadow-2xl animate-in zoom-in-95">
                  <h3 className="text-xl font-bold text-white mb-4">Adjust Credits</h3>
                  <div className="space-y-4">
                      <div>
                          <label className="text-xs text-gray-400 block mb-1">Amount</label>
                          <input 
                             type="number" 
                             min="1"
                             value={creditAmount}
                             onChange={e => setCreditAmount(parseInt(e.target.value))}
                             className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                             placeholder="0"
                          />
                      </div>
                      <div>
                          <label className="text-xs text-gray-400 block mb-1">Reason / Description</label>
                          <input 
                             type="text" 
                             value={creditReason}
                             onChange={e => setCreditReason(e.target.value)}
                             className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                             placeholder="e.g. Refund, Bonus..."
                          />
                      </div>
                      <div className="grid grid-cols-2 gap-3 pt-2">
                          <button 
                             onClick={() => handleAdjustCredits(true)} // Add
                             disabled={processingAction}
                             className="bg-green-600/20 text-green-400 border border-green-600/30 hover:bg-green-600/30 py-2 rounded-lg font-bold text-sm"
                          >
                             Add (+)
                          </button>
                          <button 
                             onClick={() => handleAdjustCredits(false)} // Remove
                             disabled={processingAction}
                             className="bg-red-600/20 text-red-400 border border-red-600/30 hover:bg-red-600/30 py-2 rounded-lg font-bold text-sm"
                          >
                             Remove (-)
                          </button>
                      </div>
                  </div>
                  <button onClick={() => setShowCreditModal(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white"><X className="w-5 h-5"/></button>
              </div>
          </div>
      )}

      {/* --- PLAN CHANGE MODAL --- */}
      {showPlanModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowPlanModal(false)}></div>
              <div className="relative z-10 w-full max-w-sm bg-gray-900 border border-white/10 rounded-2xl p-6 shadow-2xl animate-in zoom-in-95">
                  <h3 className="text-xl font-bold text-white mb-4">Manage Plan</h3>
                  <p className="text-sm text-gray-400 mb-4">Current Plan: <strong className="text-white uppercase">{selectedUser?.tier}</strong></p>
                  
                  <div className="space-y-2">
                      <button onClick={() => handleChangePlan('free')} className="w-full text-left p-3 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 flex justify-between items-center group">
                          <span>Revert to FREE</span>
                          <span className="text-xs bg-gray-900 px-2 py-1 rounded text-gray-500">Revoke</span>
                      </button>
                      <div className="h-px bg-gray-800 my-2"></div>
                      {['starter', 'creator', 'pro'].map((t) => (
                          <button 
                             key={t}
                             onClick={() => handleChangePlan(t as UserTier)} 
                             className={`w-full text-left p-3 rounded-lg border flex justify-between items-center transition-all ${
                                 selectedUser?.tier === t ? 'bg-blue-600/20 border-blue-500/50 text-blue-300' : 'bg-gray-800 border-gray-700 hover:bg-gray-700 text-gray-300'
                             }`}
                          >
                             <span className="uppercase font-bold">{t}</span>
                             {selectedUser?.tier === t && <CheckCircle className="w-4 h-4" />}
                          </button>
                      ))}
                  </div>
                  <button onClick={() => setShowPlanModal(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white"><X className="w-5 h-5"/></button>
              </div>
          </div>
      )}

    </div>
  );
};

// Helper for check circle icon locally
const CheckCircle = ({className}: {className?: string}) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
);

export default AdminDashboard;
